import { useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AgGridReact } from 'ag-grid-react'
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import '../../../shared/ui/ag-grid-theme.css'
import type {
  ColDef,
  ColumnResizedEvent,
  DragStoppedEvent,
  SortChangedEvent,
  SelectionChangedEvent,
} from 'ag-grid-community'
import { ROLE_LABELS } from '@crm/shared'
import type { OrderDetailDto, Role } from '@crm/shared'
import { assignHandler, getHandlerCandidates } from '../../../shared/api/orders.api'
import { CheckboxSetFilter } from '../../table/CheckboxSetFilter'
import { AG_GRID_LOCALE_RU } from '../../../shared/ag-grid/locale'
import { useGridSelection } from '../../../shared/hooks/useGridSelection'
import { useAuthStore } from '../../auth/authStore'
import { useOrderColState } from '../hooks/useOrderColState'
import { useOrderPositionEdit } from '../hooks/useOrderPositionEdit'
import { toast } from '../../../shared/ui/toastStore'
import { buildOrderPositionColDefs, toPosRow, type PosRow } from './orderPositionsColDefs'
import { AddOrderPositionsModal } from './AddOrderPositionsModal'
import { RemoveOrderPositionsModal } from './RemoveOrderPositionsModal'

ModuleRegistry.registerModules([AllCommunityModule])

/**
 * «Позиции заказа» — позиции заявки в «Счёте» с выбранной квотой. Отметив
 * строки, назначают «Сопровод». С 29.09.2026 до полной оплаты (canEdit) заказ
 * меняется здесь же: артикул, бренд, количество и цена клиенту — правкой в
 * ячейке, «Добавить позиции» и «Убрать из заказа» (docs/features/order-editing.md).
 */
export function OrderPositionsSection({
  order,
  canMutate,
  canEdit,
}: {
  order: OrderDetailDto
  canMutate: boolean
  canEdit: boolean
}) {
  const qc = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const orderId = order.id
  const positions = order.positions
  const gridRef = useRef<AgGridReact>(null)
  const { onCellMouseDown, onCellMouseOver, getCellStyle } = useGridSelection(gridRef)
  // v2 — добавлена колонка «Наименование» (сегмент партномера).
  const { saveState, onGridReady } = useOrderColState('crm_order_positions_cols_v2', user?.id)
  const { onCellValueChanged } = useOrderPositionEdit(orderId)
  const [selected, setSelected] = useState<string[]>([])
  const [handlerId, setHandlerId] = useState('')
  const [addOpen, setAddOpen] = useState(false)
  const [removeOpen, setRemoveOpen] = useState(false)
  // Быстрый локальный поиск по таблице (парт/наименование/бренд/поставщик…).
  const [search, setSearch] = useState('')

  const rowData = useMemo<PosRow[]>(() => positions.map(toPosRow), [positions])

  const candidates = useQuery({
    queryKey: ['orders', 'handler-candidates'],
    queryFn: getHandlerCandidates,
    enabled: canMutate,
    staleTime: 5 * 60_000,
  })

  const clearSelection = () => {
    gridRef.current?.api?.deselectAll()
    setSelected([])
  }

  const assign = useMutation({
    mutationFn: (vars: { handlerId: string | null }) =>
      assignHandler(orderId, { requestItemIds: selected, handlerId: vars.handlerId }),
    onSuccess: () => {
      clearSelection()
      setHandlerId('')
      void qc.invalidateQueries({ queryKey: ['orders', orderId] })
      // «Сопровод» пишется и в «Назначен» позиции заявки — обновляем её грид.
      void qc.invalidateQueries({ queryKey: ['request-items-by-request'] })
    },
    // Раньше onError не было: отказ API (403, 422) выглядел как кнопка, которая
    // ничего не делает. Выбор сбрасываем — кандидата могли заблокировать, и в
    // обновлённом справочнике его уже нет; иначе повторный клик послал бы тот же id.
    onError: (e) => {
      const err = e as { response?: { data?: { error?: { message?: string } } } }
      toast.error(err.response?.data?.error?.message ?? 'Не удалось назначить сопровода')
      setHandlerId('')
      void qc.invalidateQueries({ queryKey: ['orders', 'handler-candidates'] })
    },
  })

  const colDefs = useMemo(
    () => buildOrderPositionColDefs(canEdit, getCellStyle),
    [canEdit, getCellStyle],
  )

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      suppressMovable: false,
      filter: CheckboxSetFilter,
      floatingFilter: false,
      cellStyle: getCellStyle,
    }),
    [getCellStyle],
  )

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-1 flex flex-wrap items-center gap-3">
        <h2 className="text-base font-semibold">Позиции заказа ({positions.length})</h2>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск: арт./наименование/бренд…"
          className="w-64 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="text-xs text-gray-400 hover:text-gray-700"
            title="Очистить поиск"
          >
            × Очистить
          </button>
        )}
        {canEdit && (
          <button
            className="ml-auto rounded-lg bg-brand-600 px-3 py-1.5 text-sm text-white hover:bg-brand-700"
            onClick={() => setAddOpen(true)}
          >
            + Добавить позиции
          </button>
        )}
      </div>
      <p className="mb-1 text-xs text-gray-500">
        Выбранные для КП квоты. «Сопровод» — кто закупает: закупщик (Китай/Производители) или
        менеджер (Склад/РФ/Стоки). Колонки можно растягивать и менять местами; ячейки — выделять и
        копировать (Ctrl/Cmd+C). Фильтр по столбцу — по значку-воронке в заголовке.
      </p>
      {canEdit ? (
        <p className="mb-3 text-xs text-amber-700">
          Заказ можно менять до полной оплаты: артикул, бренд, количество и цена клиенту — двойной
          клик по жёлтой ячейке (Enter — сохранить, Esc — отменить); отметьте строки, чтобы убрать
          их из заказа. Строки счетов меняются вместе с заказом — после изменений скачайте шаблон
          счёта заново и обновите счёт в УНФ.
        </p>
      ) : (
        canMutate && (
          <p className="mb-3 text-xs text-gray-500">
            Состав заказа меняется до полной оплаты — в этом статусе только просмотр.
          </p>
        )
      )}

      {canMutate && selected.length > 0 && (
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-600">Выбрано {selected.length}:</span>
          <select
            className="rounded border border-gray-300 px-2 py-1.5 text-sm"
            value={handlerId}
            onChange={(e) => setHandlerId(e.target.value)}
          >
            <option value="">— сопровод —</option>
            {(candidates.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name ?? c.id.slice(0, 6)} ({ROLE_LABELS[c.role as Role] ?? c.role})
              </option>
            ))}
          </select>
          <button
            className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-60"
            onClick={() => assign.mutate({ handlerId: handlerId || null })}
            disabled={!handlerId || assign.isPending}
          >
            Назначить
          </button>
          <button
            className="rounded-lg bg-gray-100 px-3 py-1.5 text-sm hover:bg-gray-200 disabled:opacity-60"
            onClick={() => assign.mutate({ handlerId: null })}
            disabled={assign.isPending}
            title="Сбросить к авто-сопроводу по матрице"
          >
            Авто
          </button>
          {canEdit && (
            <button
              className="rounded-lg bg-red-100 px-3 py-1.5 text-sm text-red-700 hover:bg-red-200"
              onClick={() => setRemoveOpen(true)}
            >
              Убрать из заказа
            </button>
          )}
        </div>
      )}

      {positions.length === 0 ? (
        <p className="text-sm text-gray-400">Позиций в счетах нет.</p>
      ) : (
        <div className="crm-table ag-theme-alpine overflow-hidden rounded-lg border border-gray-200 bg-white h-[360px]">
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={colDefs}
            defaultColDef={defaultColDef}
            localeText={AG_GRID_LOCALE_RU}
            quickFilterText={search}
            getRowId={(p) => p.data.requestItemId}
            rowSelection={
              canMutate
                ? {
                    mode: 'multiRow',
                    enableClickSelection: false,
                    checkboxes: true,
                    headerCheckbox: true,
                    // Шапка-чекбокс выбирает только отфильтрованные строки, а не все.
                    selectAll: 'filtered',
                  }
                : undefined
            }
            onSelectionChanged={(e: SelectionChangedEvent) =>
              setSelected(e.api.getSelectedRows().map((r: PosRow) => r.requestItemId))
            }
            onGridReady={onGridReady}
            onCellMouseDown={onCellMouseDown}
            onCellMouseOver={onCellMouseOver}
            onCellValueChanged={onCellValueChanged}
            onDragStopped={(e: DragStoppedEvent) => saveState(e.api)}
            onColumnResized={(e: ColumnResizedEvent) => {
              if (e.finished) saveState(e.api)
            }}
            onSortChanged={(e: SortChangedEvent) => saveState(e.api)}
            rowHeight={40}
            headerHeight={40}
            suppressDragLeaveHidesColumns
            // Без правки ячейки не фокусируются — как было; с правкой фокус нужен
            // для Enter/двойного клика.
            suppressCellFocus={!canEdit}
            stopEditingWhenCellsLoseFocus
          />
        </div>
      )}

      {addOpen && <AddOrderPositionsModal order={order} onClose={() => setAddOpen(false)} />}
      {removeOpen && (
        <RemoveOrderPositionsModal
          order={order}
          requestItemIds={selected}
          onClose={() => setRemoveOpen(false)}
          onDone={() => {
            setRemoveOpen(false)
            clearSelection()
          }}
        />
      )}
    </section>
  )
}
